import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, LoaderCircle, Share2, X } from 'lucide-react';
import {
  ApiError,
  createChallenge,
  fetchSessionShare,
  fetchShareClipExport,
  recordChallengeShareInitiation,
} from '@/api/teleopApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type {
  ChallengeShareInitiationMethod,
  ChallengeShareTarget,
  ChallengeResponse,
  ShareAttemptChallenge,
  ShareAttemptComposite,
  ShareResultKind,
  ShareSessionAttempt,
  ShareSessionResponse,
} from '@/contracts';
import { useClerkToken } from '@/hooks/useClerkToken';
import { PLAY_CLAW_JWT_TEMPLATE } from '@/lib/auth';
import {
  trackChallengeShareInitiated,
  trackChallengeShareSheetOpened,
} from '@/lib/analytics';
import {
  buildChallengeShareLink,
  CHALLENGE_SHARE_LINK_OPTIONS,
  formatChallengeShareTargetLabel,
} from '@/lib/challengeShareLinks';
import { logInfo, logWarn } from '@/lib/logger';
import { enterMixpanelReplaySurface, leaveMixpanelReplaySurface } from '@/lib/mixpanel';
import { cn } from '@/lib/utils';

const POLL_INTERVAL_MS = 2500;
const DOWNLOAD_FEEDBACK_TIMEOUT_MS = 2200;
const SHARE_FILE_MIME_TYPE = 'video/mp4';

type ShareClipsSheetProps = {
  sessionId: string;
  onClose: () => void;
};

type ChallengeSummary = Pick<
  ChallengeResponse,
  | 'challenge_id'
  | 'public_id'
  | 'challenge_url'
  | 'default_utm_source'
  | 'default_utm_medium'
  | 'default_utm_campaign'
  | 'default_utm_content'
  | 'default_utm_id'
  | 'cta_headline'
  | 'cta_label'
>;

type PreparedShareFileStatus = 'idle' | 'loading' | 'ready' | 'error';
type ChallengeSummaryByAttemptId = Record<string, ChallengeSummary>;
type ChallengeStatus = 'idle' | 'loading' | 'ready' | 'error';
type ChallengeStatusByAttemptId = Record<string, ChallengeStatus>;
type DisplayStatus = 'preparing' | 'queued' | 'rendering' | 'ready' | 'failed';
type PreparedShareFileState = {
  status: PreparedShareFileStatus;
  attemptId: string | null;
  exportUrl: string | null;
  shareFile: File | null;
};

const buildInitialPreparedShareFileState = (): PreparedShareFileState => {
  const preparedShareFileState = {
    status: 'idle' as const,
    attemptId: null,
    exportUrl: null,
    shareFile: null,
  };
  return preparedShareFileState;
};

const isReadyComposite = (composite: ShareAttemptComposite | null | undefined) =>
  composite?.status === 'ready' &&
  Boolean(composite.preview_url) &&
  Boolean(composite.download_url);

const isPreviewReadyComposite = (
  composite: ShareAttemptComposite | null | undefined
) => composite?.status === 'ready' && Boolean(composite.preview_url);

const isDownloadReadyComposite = (
  composite: ShareAttemptComposite | null | undefined
) => composite?.status === 'ready' && Boolean(composite.download_url);

const isPendingComposite = (composite: ShareAttemptComposite | null | undefined) =>
  composite == null ||
  composite.status === 'queued' ||
  composite.status === 'rendering';

const toChallengeSummary = (
  challenge: ChallengeResponse | ShareAttemptChallenge
): ChallengeSummary => {
  const challengeSummary = {
    challenge_id: challenge.challenge_id,
    public_id: challenge.public_id,
    challenge_url: challenge.challenge_url,
    default_utm_source: challenge.default_utm_source,
    default_utm_medium: challenge.default_utm_medium,
    default_utm_campaign: challenge.default_utm_campaign,
    default_utm_content: challenge.default_utm_content,
    default_utm_id: challenge.default_utm_id,
    cta_headline: challenge.cta_headline,
    cta_label: challenge.cta_label,
  };
  return challengeSummary;
};

const selectChallenge = (
  attempt: ShareSessionAttempt | null,
  challengeByAttemptId: ChallengeSummaryByAttemptId
): ChallengeSummary | null => {
  if (attempt === null) {
    return null;
  }

  const serverChallenge = attempt.challenge;
  if (serverChallenge) {
    return toChallengeSummary(serverChallenge);
  }

  const localChallenge = challengeByAttemptId[attempt.attempt_id];
  if (localChallenge) {
    return localChallenge;
  }

  return null;
};

const selectDefaultAttemptId = (shareSession: ShareSessionResponse): string | null => {
  const orderedAttempts = [...shareSession.attempts].sort(
    (left, right) => right.attempt_no - left.attempt_no
  );
  const latestWinningReady = orderedAttempts.find(
    (attempt) =>
      attempt.result_kind === 'win' && isReadyComposite(attempt.composite)
  );
  if (latestWinningReady) {
    return latestWinningReady.attempt_id;
  }

  const latestReady = orderedAttempts.find((attempt) =>
    isReadyComposite(attempt.composite)
  );
  if (latestReady) {
    return latestReady.attempt_id;
  }

  return shareSession.attempts[0]?.attempt_id ?? null;
};

const formatResultLabel = (resultKind: ShareResultKind | null | undefined) => {
  switch (resultKind) {
    case 'win':
      return 'Win';
    case 'lose':
      return 'Miss';
    case 'partial':
      return 'Partial';
    case 'score':
      return 'Score';
    case 'time':
      return 'Timed';
    default:
      return 'Attempt';
  }
};

const formatDurationLabel = (durationMs: number | null | undefined) => {
  if (!durationMs || durationMs <= 0) {
    return null;
  }

  const roundedSeconds = Math.max(1, Math.round(durationMs / 1000));
  return `${roundedSeconds}s clip`;
};

const buildDownloadFilename = (attemptNo: number) =>
  `playclaw-attempt-${attemptNo + 1}.mp4`;

const buildShareFile = (blob: Blob, attemptNo: number) => {
  const fileName = buildDownloadFilename(attemptNo);
  const fileType = blob.type || SHARE_FILE_MIME_TYPE;
  const shareFile = new File([blob], fileName, { type: fileType });
  return shareFile;
};

const browserSupportsNativeFileShare = () => {
  const hasNavigator = typeof navigator !== 'undefined';
  if (!hasNavigator) {
    return false;
  }

  const hasShareApi = typeof navigator.share === 'function';
  if (!hasShareApi) {
    return false;
  }

  const hasFileConstructor = typeof File !== 'undefined';
  if (!hasFileConstructor) {
    return false;
  }

  const hasCanShareApi = typeof navigator.canShare === 'function';
  if (!hasCanShareApi) {
    return true;
  }

  const sampleFile = new File([''], 'playclaw-share.mp4', {
    type: SHARE_FILE_MIME_TYPE,
  });
  const sampleShareData: ShareData = {
    files: [sampleFile],
  };
  const canShareFiles = navigator.canShare(sampleShareData);
  return canShareFiles;
};

const isPreparedShareFileStateForSelection = (
  preparedShareFileState: PreparedShareFileState,
  attemptId: string | null,
  exportUrl: string | null
) => {
  const matchesAttemptId = preparedShareFileState.attemptId === attemptId;
  const matchesExportUrl = preparedShareFileState.exportUrl === exportUrl;
  return matchesAttemptId && matchesExportUrl;
};

const selectPreviewComposite = (
  attempt: ShareSessionAttempt | null
): ShareAttemptComposite | null => {
  if (attempt === null) {
    return null;
  }

  const challengeComposite = attempt.challenge_composite;
  if (isPreviewReadyComposite(challengeComposite)) {
    return challengeComposite ?? null;
  }

  return attempt.composite ?? null;
};

const selectShareComposite = (
  attempt: ShareSessionAttempt | null
): ShareAttemptComposite | null => {
  if (attempt === null) {
    return null;
  }

  return attempt.challenge_composite ?? null;
};

const resolveServerChallengeStatus = (
  attempt: ShareSessionAttempt
): ChallengeStatus | null => {
  const challengeComposite = attempt.challenge_composite;
  if (challengeComposite?.status === 'ready') {
    return 'ready';
  }

  if (challengeComposite?.status === 'failed') {
    return 'error';
  }

  if (attempt.challenge || challengeComposite) {
    return 'loading';
  }

  return null;
};

const resolveDisplayStatus = ({
  previewComposite,
  shareComposite,
  challenge,
  challengeStatus,
}: {
  previewComposite: ShareAttemptComposite | null;
  shareComposite: ShareAttemptComposite | null;
  challenge: ChallengeSummary | null;
  challengeStatus: ChallengeStatus;
}): DisplayStatus => {
  if (shareComposite) {
    return shareComposite.status;
  }

  if (challengeStatus === 'error') {
    return 'failed';
  }

  if (challenge || challengeStatus === 'loading') {
    return 'rendering';
  }

  if (previewComposite) {
    return previewComposite.status;
  }

  return 'preparing';
};

const formatDisplayStatusLabel = (status: DisplayStatus) => {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'rendering':
      return 'Rendering';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return 'Preparing';
  }
};

const displayStatusToneClass = (status: DisplayStatus) => {
  switch (status) {
    case 'ready':
      return 'border-emerald-300/30 bg-emerald-400/15 text-emerald-100';
    case 'failed':
      return 'border-rose-300/30 bg-rose-400/15 text-rose-100';
    case 'queued':
    case 'rendering':
      return 'border-amber-300/30 bg-amber-400/15 text-amber-100';
    default:
      return 'border-white/12 bg-white/10 text-white/75';
  }
};

const hasPendingAttempts = (
  shareSession: ShareSessionResponse | null,
  challengeByAttemptId: ChallengeSummaryByAttemptId,
  challengeStatusByAttemptId: ChallengeStatusByAttemptId
) =>
  Boolean(
    shareSession?.attempts.some((attempt) => {
      const previewPending = isPendingComposite(attempt.composite);
      if (previewPending) {
        return true;
      }

      const attemptChallenge = selectChallenge(attempt, challengeByAttemptId);
      const localChallengeStatus =
        challengeStatusByAttemptId[attempt.attempt_id] ?? 'idle';

      if (localChallengeStatus === 'loading') {
        return true;
      }

      if (!attemptChallenge) {
        return false;
      }

      return isPendingComposite(attempt.challenge_composite);
    })
  );

const buildShareText = (
  resultKind: ShareResultKind | null | undefined,
  challengeUrl: string
) => {
  const prefix =
    resultKind === 'win'
      ? 'I just won a claw bot round.'
      : 'I just played the claw bot.';
  return `${prefix} Think you can do better? ${challengeUrl}`;
};

const ShareClipsSheet: React.FC<ShareClipsSheetProps> = ({
  sessionId,
  onClose,
}) => {
  const { getToken, isLoaded, isSignedIn } = useClerkToken(PLAY_CLAW_JWT_TEMPLATE);
  const [shareSession, setShareSession] = useState<ShareSessionResponse | null>(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);
  const [selectedShareTarget, setSelectedShareTarget] =
    useState<ChallengeShareTarget>('direct');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [shareFilePreparationNonce, setShareFilePreparationNonce] = useState(0);
  const [preparedShareFileState, setPreparedShareFileState] =
    useState<PreparedShareFileState>(buildInitialPreparedShareFileState);
  const [challengeByAttemptId, setChallengeByAttemptId] =
    useState<ChallengeSummaryByAttemptId>({});
  const [challengeStatusByAttemptId, setChallengeStatusByAttemptId] =
    useState<ChallengeStatusByAttemptId>({});
  const feedbackTimeoutRef = useRef<number | null>(null);
  const selectedAttemptManuallyRef = useRef(false);
  const requestInFlightRef = useRef(false);
  const preparedShareFileStateRef = useRef(preparedShareFileState);
  const challengeRequestInFlightRef = useRef(new Set<string>());
  const trackedChallengeAttemptIdsRef = useRef(new Set<string>());

  useEffect(() => {
    enterMixpanelReplaySurface('share');

    return () => {
      leaveMixpanelReplaySurface('share');
    };
  }, []);
  const isMountedRef = useRef(true);

  const clearFeedbackTimeout = useCallback(() => {
    if (feedbackTimeoutRef.current !== null) {
      window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
  }, []);

  const scheduleFeedbackClear = useCallback(
    (message: string) => {
      clearFeedbackTimeout();
      setShareFeedback(message);
      feedbackTimeoutRef.current = window.setTimeout(() => {
        feedbackTimeoutRef.current = null;
        setShareFeedback(null);
      }, DOWNLOAD_FEEDBACK_TIMEOUT_MS);
    },
    [clearFeedbackTimeout]
  );

  const updateChallengeStatus = useCallback(
    (attemptId: string, status: ChallengeStatus) => {
      setChallengeStatusByAttemptId((currentStatuses) => {
        const previousStatus = currentStatuses[attemptId];
        if (previousStatus === status) {
          return currentStatuses;
        }

        const nextStatuses = {
          ...currentStatuses,
          [attemptId]: status,
        };
        return nextStatuses;
      });
    },
    []
  );

  const loadShareSession = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!isLoaded) {
        return;
      }

      if (requestInFlightRef.current) {
        return;
      }

      if (!isSignedIn) {
        setLoadError('Could not load your clips right now.');
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      if (options?.silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      requestInFlightRef.current = true;

      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Missing auth token');
        }

        const nextShareSession = await fetchSessionShare(sessionId, token);
        setShareSession(nextShareSession);
        setLoadError(null);
      } catch (error) {
        setLoadError('Could not load your clips right now.');
        logWarn('share.load.failed', 'Failed to load session share clips', {
          session_id: sessionId,
          status: error instanceof ApiError ? error.status : undefined,
          code: error instanceof ApiError ? error.code : undefined,
        });
      } finally {
        requestInFlightRef.current = false;
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [getToken, isLoaded, isSignedIn, sessionId]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    logInfo('share.overlay.opened', 'Player opened share clips overlay', {
      session_id: sessionId,
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearFeedbackTimeout();
      logInfo('share.overlay.closed', 'Player closed share clips overlay', {
        session_id: sessionId,
      });
    };
  }, [clearFeedbackTimeout, onClose, sessionId]);

  useEffect(() => {
    void loadShareSession();
  }, [loadShareSession]);

  useEffect(() => {
    if (!shareSession) {
      return;
    }

    setChallengeByAttemptId((currentChallenges) => {
      let hasChanges = false;
      const nextChallenges = { ...currentChallenges };

      for (const attempt of shareSession.attempts) {
        const serverChallenge = attempt.challenge;
        if (!serverChallenge) {
          continue;
        }

        const nextChallenge = toChallengeSummary(serverChallenge);
        const previousChallenge = currentChallenges[attempt.attempt_id];
        const previousPublicId = previousChallenge?.public_id ?? null;
        if (previousPublicId === nextChallenge.public_id) {
          continue;
        }

        nextChallenges[attempt.attempt_id] = nextChallenge;
        hasChanges = true;
      }

      if (!hasChanges) {
        return currentChallenges;
      }

      return nextChallenges;
    });

    setChallengeStatusByAttemptId((currentStatuses) => {
      let hasChanges = false;
      const nextStatuses = { ...currentStatuses };

      for (const attempt of shareSession.attempts) {
        const nextStatus = resolveServerChallengeStatus(attempt);
        if (nextStatus === null) {
          continue;
        }

        const previousStatus = currentStatuses[attempt.attempt_id];
        if (previousStatus === nextStatus) {
          continue;
        }

        nextStatuses[attempt.attempt_id] = nextStatus;
        hasChanges = true;
      }

      if (!hasChanges) {
        return currentStatuses;
      }

      return nextStatuses;
    });
  }, [shareSession]);

  useEffect(() => {
    if (!shareSession) {
      return;
    }

    const defaultAttemptId = selectDefaultAttemptId(shareSession);
    if (!selectedAttemptId) {
      setSelectedAttemptId(defaultAttemptId);
      return;
    }

    const currentAttempt = shareSession.attempts.find(
      (attempt) => attempt.attempt_id === selectedAttemptId
    );
    if (!currentAttempt) {
      setSelectedAttemptId(defaultAttemptId);
      selectedAttemptManuallyRef.current = false;
      return;
    }

    if (selectedAttemptManuallyRef.current) {
      return;
    }

    const defaultAttempt =
      shareSession.attempts.find(
        (attempt) => attempt.attempt_id === defaultAttemptId
      ) ?? null;
    if (
      isPendingComposite(currentAttempt.composite) &&
      isPreviewReadyComposite(defaultAttempt?.composite) &&
      defaultAttemptId !== currentAttempt.attempt_id
    ) {
      setSelectedAttemptId(defaultAttemptId);
    }
  }, [selectedAttemptId, shareSession]);

  useEffect(() => {
    const hasPending = hasPendingAttempts(
      shareSession,
      challengeByAttemptId,
      challengeStatusByAttemptId
    );
    if (!shareSession || !hasPending) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadShareSession({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    challengeByAttemptId,
    challengeStatusByAttemptId,
    loadShareSession,
    shareSession,
  ]);

  const selectedAttempt = useMemo(() => {
    const matchingAttempt =
      shareSession?.attempts.find(
        (attempt) => attempt.attempt_id === selectedAttemptId
      ) ?? null;
    return matchingAttempt;
  }, [selectedAttemptId, shareSession]);

  const selectedChallenge = useMemo(
    () => selectChallenge(selectedAttempt, challengeByAttemptId),
    [challengeByAttemptId, selectedAttempt]
  );
  const selectedShareLink = useMemo(() => {
    if (selectedChallenge === null) {
      return null;
    }

    const shareLink = buildChallengeShareLink(
      selectedChallenge,
      selectedShareTarget
    );
    return shareLink;
  }, [selectedChallenge, selectedShareTarget]);
  const selectedShareTargetLabel = useMemo(() => {
    const targetLabel = formatChallengeShareTargetLabel(selectedShareTarget);
    return targetLabel;
  }, [selectedShareTarget]);

  const selectedPreviewComposite = useMemo(
    () => selectPreviewComposite(selectedAttempt),
    [selectedAttempt]
  );

  const selectedShareComposite = useMemo(
    () => selectShareComposite(selectedAttempt),
    [selectedAttempt]
  );

  const selectedChallengeStatus = selectedAttempt
    ? challengeStatusByAttemptId[selectedAttempt.attempt_id] ?? 'idle'
    : 'idle';

  const selectedDisplayStatus = resolveDisplayStatus({
    previewComposite: selectedPreviewComposite,
    shareComposite: selectedShareComposite,
    challenge: selectedChallenge,
    challengeStatus: selectedChallengeStatus,
  });

  const selectedStatusLabel = formatDisplayStatusLabel(selectedDisplayStatus);
  const selectedStatusToneClass = displayStatusToneClass(selectedDisplayStatus);
  const selectedDurationMs =
    selectedShareComposite?.duration_ms ?? selectedPreviewComposite?.duration_ms;
  const selectedDurationLabel = formatDurationLabel(selectedDurationMs);
  const showShareButton =
    selectedChallenge !== null && isDownloadReadyComposite(selectedShareComposite);
  const canRefresh = !isLoading && !isRefreshing;
  const nativeFileShareSupported = useMemo(
    () => browserSupportsNativeFileShare(),
    []
  );
  const selectedAttemptIdValue = selectedAttempt?.attempt_id ?? null;
  const selectedAttemptNo = selectedAttempt?.attempt_no ?? null;
  const selectedChallengePublicId = selectedChallenge?.public_id ?? null;
  const selectedExportUrl = selectedShareComposite?.export_url ?? null;
  const nativeFileShareAvailable =
    nativeFileShareSupported && Boolean(selectedExportUrl);
  const preparedShareFileMatchesSelection = isPreparedShareFileStateForSelection(
    preparedShareFileState,
    selectedAttemptIdValue,
    selectedExportUrl
  );
  const preparedShareFile =
    preparedShareFileMatchesSelection &&
    preparedShareFileState.status === 'ready'
      ? preparedShareFileState.shareFile
      : null;
  const nativeSharePreparationFailed =
    preparedShareFileMatchesSelection &&
    preparedShareFileState.status === 'error';
  const canUseNativeFileShare =
    nativeFileShareAvailable && preparedShareFile !== null;
  const isPreparingNativeShare =
    nativeFileShareAvailable &&
    !canUseNativeFileShare &&
    !nativeSharePreparationFailed;
  const primaryActionLabel = isPreparingNativeShare
    ? 'Preparing video'
    : canUseNativeFileShare
      ? 'Share challenge'
      : 'Download challenge';
  const isPrimaryActionDisabled = isExporting || isPreparingNativeShare;
  const exportInProgressLabel = canUseNativeFileShare
    ? 'Opening share'
    : 'Starting download';

  const selectedPreviewKey =
    selectedAttempt && selectedPreviewComposite
      ? `${selectedAttempt.attempt_id}:${selectedPreviewComposite.template_version}`
      : selectedAttempt?.attempt_id ?? 'challenge-preview';

  const statusDescription = (() => {
    if (selectedDurationLabel) {
      return selectedDurationLabel;
    }

    if (selectedDisplayStatus === 'failed') {
      return 'Try another attempt.';
    }

    if (selectedChallenge) {
      return 'Preparing the QR-enabled challenge version.';
    }

    return 'Pick the clip you want to post.';
  })();

  const challengeHint = (() => {
    if (!selectedAttempt) {
      return 'Pick an attempt to prepare a challenge link.';
    }

    if (selectedChallengeStatus === 'error') {
      return 'Could not prepare a challenge version right now.';
    }

    if (!selectedChallenge) {
      return 'Preparing your challenge link...';
    }

    if (selectedDisplayStatus === 'failed') {
      return 'This challenge version could not be prepared.';
    }

    if (!showShareButton) {
      return 'Preparing challenge version with QR...';
    }

    return `Ready to share with a ${selectedShareTargetLabel} challenge link and QR.`;
  })();

  useEffect(() => {
    preparedShareFileStateRef.current = preparedShareFileState;
  }, [preparedShareFileState]);

  useEffect(() => {
    if (!selectedAttempt) {
      return;
    }

    const attemptId = selectedAttempt.attempt_id;
    const existingChallenge = selectChallenge(selectedAttempt, challengeByAttemptId);
    if (existingChallenge) {
      return;
    }

    if (!isLoaded || !isSignedIn) {
      return;
    }

    const currentChallengeStatus = challengeStatusByAttemptId[attemptId] ?? 'idle';
    if (currentChallengeStatus !== 'idle') {
      return;
    }

    if (challengeRequestInFlightRef.current.has(attemptId)) {
      return;
    }

    challengeRequestInFlightRef.current.add(attemptId);
    updateChallengeStatus(attemptId, 'loading');

    const ensureChallenge = async () => {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Missing auth token');
        }

        const challengeResponse = await createChallenge(
          {
            attempt_id: attemptId,
            share_target: 'share_sheet',
          },
          token
        );

        if (!isMountedRef.current) {
          return;
        }

        const nextChallenge = toChallengeSummary(challengeResponse);
        setChallengeByAttemptId((currentChallenges) => {
          const nextChallenges = {
            ...currentChallenges,
            [attemptId]: nextChallenge,
          };
          return nextChallenges;
        });
        updateChallengeStatus(attemptId, 'loading');
        void loadShareSession({ silent: true });
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }

        updateChallengeStatus(attemptId, 'error');
        logWarn(
          'share.challenge.prepare_failed',
          'Failed to prepare share challenge',
          {
            session_id: sessionId,
            attempt_id: attemptId,
            status: error instanceof ApiError ? error.status : undefined,
            code: error instanceof ApiError ? error.code : undefined,
          }
        );
      } finally {
        challengeRequestInFlightRef.current.delete(attemptId);
      }
    };

    void ensureChallenge();
  }, [
    challengeByAttemptId,
    challengeStatusByAttemptId,
    getToken,
    isLoaded,
    isSignedIn,
    loadShareSession,
    selectedAttempt,
    sessionId,
    updateChallengeStatus,
  ]);

  useEffect(() => {
    if (!selectedAttempt || !selectedChallenge) {
      return;
    }

    const attemptId = selectedAttempt.attempt_id;
    if (trackedChallengeAttemptIdsRef.current.has(attemptId)) {
      return;
    }

    trackChallengeShareSheetOpened({
      sessionId,
      attemptId,
      attemptNo: selectedAttempt.attempt_no,
      challengePublicId: selectedChallenge.public_id,
    });
    trackedChallengeAttemptIdsRef.current.add(attemptId);
  }, [selectedAttempt, selectedChallenge, sessionId]);

  useEffect(() => {
    const shouldPrepareNativeShare =
      showShareButton &&
      nativeFileShareAvailable &&
      selectedAttemptIdValue !== null &&
      selectedAttemptNo !== null &&
      selectedChallengePublicId !== null &&
      selectedExportUrl !== null;

    if (!shouldPrepareNativeShare) {
      setPreparedShareFileState((currentPreparedShareFileState) => {
        const isAlreadyIdle = currentPreparedShareFileState.status === 'idle';
        if (isAlreadyIdle) {
          return currentPreparedShareFileState;
        }

        const initialPreparedShareFileState = buildInitialPreparedShareFileState();
        return initialPreparedShareFileState;
      });
      return;
    }

    const currentPreparedShareFileState = preparedShareFileStateRef.current;
    const preparedShareFileMatchesCurrentSelection =
      isPreparedShareFileStateForSelection(
        currentPreparedShareFileState,
        selectedAttemptIdValue,
        selectedExportUrl
      );
    const preparedShareFileReady =
      preparedShareFileMatchesCurrentSelection &&
      currentPreparedShareFileState.status === 'ready';
    if (preparedShareFileReady) {
      return;
    }

    const preparedShareFileLoading =
      preparedShareFileMatchesCurrentSelection &&
      currentPreparedShareFileState.status === 'loading';
    if (preparedShareFileLoading) {
      return;
    }

    const preparedShareFileFailed =
      preparedShareFileMatchesCurrentSelection &&
      currentPreparedShareFileState.status === 'error';
    if (preparedShareFileFailed) {
      return;
    }

    const loadingPreparedShareFileState: PreparedShareFileState = {
      status: 'loading',
      attemptId: selectedAttemptIdValue,
      exportUrl: selectedExportUrl,
      shareFile: null,
    };
    setPreparedShareFileState(loadingPreparedShareFileState);

    const abortController = new AbortController();
    let didCancel = false;

    const prepareShareFile = async () => {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Missing auth token');
        }

        const blob = await fetchShareClipExport(
          selectedExportUrl,
          token,
          abortController.signal
        );
        if (didCancel || !isMountedRef.current) {
          return;
        }

        const shareFile = buildShareFile(blob, selectedAttemptNo);
        const readyPreparedShareFileState: PreparedShareFileState = {
          status: 'ready',
          attemptId: selectedAttemptIdValue,
          exportUrl: selectedExportUrl,
          shareFile,
        };
        setPreparedShareFileState(readyPreparedShareFileState);
      } catch (error) {
        if (didCancel || !isMountedRef.current) {
          return;
        }

        const wasAborted =
          error instanceof DOMException && error.name === 'AbortError';
        if (wasAborted) {
          return;
        }

        const failedPreparedShareFileState: PreparedShareFileState = {
          status: 'error',
          attemptId: selectedAttemptIdValue,
          exportUrl: selectedExportUrl,
          shareFile: null,
        };
        setPreparedShareFileState(failedPreparedShareFileState);
        logWarn(
          'share.native_share.prepare_failed',
          'Failed to prepare native challenge share file',
          {
            session_id: sessionId,
            attempt_id: selectedAttemptIdValue,
            attempt_no: selectedAttemptNo,
            challenge_public_id: selectedChallengePublicId,
            status: error instanceof ApiError ? error.status : undefined,
            code: error instanceof ApiError ? error.code : undefined,
          }
        );
      }
    };

    void prepareShareFile();

    return () => {
      didCancel = true;
      abortController.abort();
    };
  }, [
    getToken,
    nativeFileShareAvailable,
    selectedAttemptIdValue,
    selectedAttemptNo,
    selectedChallengePublicId,
    selectedExportUrl,
    sessionId,
    shareFilePreparationNonce,
    showShareButton,
  ]);

  const triggerBrowserDownload = useCallback(
    (downloadUrl: string, attemptNo: number) => {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = buildDownloadFilename(attemptNo);
      link.rel = 'noopener';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      scheduleFeedbackClear('Download starting');
    },
    [scheduleFeedbackClear]
  );

  const persistChallengeShareInitiation = useCallback(
    async (
      challengeId: string,
      method: ChallengeShareInitiationMethod,
      shareTarget: ChallengeShareTarget
    ) => {
      const token = await getToken();
      if (!token) {
        throw new Error('Missing auth token');
      }

      await recordChallengeShareInitiation(
        challengeId,
        {
          method,
          share_target: shareTarget,
        },
        token
      );
    },
    [getToken]
  );

  const finalizeChallengeShareInitiation = useCallback(
    async (
      challenge: ChallengeSummary,
      attempt: ShareSessionAttempt,
      method: ChallengeShareInitiationMethod,
      shareTarget: ChallengeShareTarget
    ) => {
      const attemptId = attempt.attempt_id;
      const attemptNo = attempt.attempt_no;
      const challengeId = challenge.challenge_id;
      const challengePublicId = challenge.public_id;
      let backendRecorded = false;

      try {
        await persistChallengeShareInitiation(challengeId, method, shareTarget);
        backendRecorded = true;
      } catch (error) {
        logWarn(
          'share.initiation.persist_failed',
          'Failed to persist challenge share initiation',
          {
            session_id: sessionId,
            attempt_id: attemptId,
            attempt_no: attemptNo,
            challenge_id: challengeId,
            challenge_public_id: challengePublicId,
            share_method: method,
            share_target: shareTarget,
            status: error instanceof ApiError ? error.status : undefined,
            code: error instanceof ApiError ? error.code : undefined,
          }
        );
      }

      trackChallengeShareInitiated({
        sessionId,
        attemptId,
        attemptNo,
        challengePublicId,
        method,
        shareTarget,
        backendRecorded,
      });
    },
    [persistChallengeShareInitiation, sessionId]
  );

  const handleCopyShareLink = useCallback(async () => {
    if (!selectedAttempt || !selectedChallenge || !selectedShareLink) {
      return;
    }

    const clipboard = navigator.clipboard;
    if (!clipboard) {
      logWarn('share.link.copy_unavailable', 'Clipboard API unavailable', {
        session_id: sessionId,
        attempt_id: selectedAttempt.attempt_id,
        challenge_public_id: selectedChallenge.public_id,
        share_target: selectedShareTarget,
      });
      scheduleFeedbackClear('Copy unavailable');
      return;
    }

    try {
      await clipboard.writeText(selectedShareLink);
      scheduleFeedbackClear(`${selectedShareTargetLabel} link copied`);
      await finalizeChallengeShareInitiation(
        selectedChallenge,
        selectedAttempt,
        'copy_link',
        selectedShareTarget
      );
      logInfo('share.link.copied', 'Player copied a tagged challenge link', {
        session_id: sessionId,
        attempt_id: selectedAttempt.attempt_id,
        attempt_no: selectedAttempt.attempt_no,
        challenge_public_id: selectedChallenge.public_id,
        share_target: selectedShareTarget,
      });
    } catch (error) {
      logWarn('share.link.copy_failed', 'Failed to copy a tagged challenge link', {
        session_id: sessionId,
        attempt_id: selectedAttempt.attempt_id,
        attempt_no: selectedAttempt.attempt_no,
        challenge_public_id: selectedChallenge.public_id,
        share_target: selectedShareTarget,
        status: error instanceof ApiError ? error.status : undefined,
        code: error instanceof ApiError ? error.code : undefined,
      });
      scheduleFeedbackClear('Copy failed');
    }
  }, [
    finalizeChallengeShareInitiation,
    scheduleFeedbackClear,
    selectedAttempt,
    selectedChallenge,
    selectedShareLink,
    selectedShareTarget,
    selectedShareTargetLabel,
    sessionId,
  ]);

  const handleAttemptSelect = (attempt: ShareSessionAttempt) => {
    selectedAttemptManuallyRef.current = true;
    setSelectedAttemptId(attempt.attempt_id);

    const compositeStatus =
      attempt.challenge_composite?.status ??
      attempt.composite?.status ??
      'preparing';
    logInfo('share.attempt.selected', 'Player selected an attempt clip', {
      session_id: sessionId,
      attempt_id: attempt.attempt_id,
      attempt_no: attempt.attempt_no,
      composite_status: compositeStatus,
    });
  };

  const handleRefresh = () => {
    if (selectedAttempt) {
      const selectedChallengeStatus =
        challengeStatusByAttemptId[selectedAttempt.attempt_id] ?? 'idle';
      if (selectedChallengeStatus === 'error') {
        updateChallengeStatus(selectedAttempt.attempt_id, 'idle');
      }
    }

    if (nativeSharePreparationFailed) {
      const initialPreparedShareFileState = buildInitialPreparedShareFileState();
      setPreparedShareFileState(initialPreparedShareFileState);
      setShareFilePreparationNonce((currentNonce) => currentNonce + 1);
    }

    void loadShareSession({ silent: true });
  };

  const handleDownload = async () => {
    if (
      !selectedAttempt ||
      !selectedChallenge ||
      !selectedShareComposite ||
      !selectedShareLink
    ) {
      return;
    }

    const downloadUrl = selectedShareComposite.download_url;
    if (!downloadUrl || isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      if (canUseNativeFileShare && preparedShareFile) {
        const shareText = buildShareText(
          selectedAttempt.result_kind,
          selectedShareLink
        );
        const shareFiles = [preparedShareFile];
        const shareData: ShareData = {
          files: shareFiles,
          title: 'PlayClaw',
          text: shareText,
        };

        const browserCanSharePreparedFile =
          typeof navigator.canShare !== 'function' ||
          navigator.canShare({ files: shareFiles });
        if (!browserCanSharePreparedFile) {
          throw new Error('Native file share unavailable');
        }

        await navigator.share(shareData);
        scheduleFeedbackClear('Share opened');
        await finalizeChallengeShareInitiation(
          selectedChallenge,
          selectedAttempt,
          'native_share',
          selectedShareTarget
        );
        logInfo(
          'share.native_share.initiated',
          'Player initiated native share for a challenge clip',
          {
            session_id: sessionId,
            attempt_id: selectedAttempt.attempt_id,
            attempt_no: selectedAttempt.attempt_no,
            challenge_public_id: selectedChallenge.public_id,
            share_target: selectedShareTarget,
          }
        );
        return;
      }

      if (isPreparingNativeShare) {
        scheduleFeedbackClear('Preparing video');
        return;
      }

      triggerBrowserDownload(downloadUrl, selectedAttempt.attempt_no);
      await finalizeChallengeShareInitiation(
        selectedChallenge,
        selectedAttempt,
        'download',
        selectedShareTarget
      );
      logInfo(
        'share.download.started',
        'Player started downloading a challenge clip',
        {
          session_id: sessionId,
          attempt_id: selectedAttempt.attempt_id,
          attempt_no: selectedAttempt.attempt_no,
          challenge_public_id: selectedChallenge.public_id,
          share_target: selectedShareTarget,
        }
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        scheduleFeedbackClear('Share cancelled');
        return;
      }

      triggerBrowserDownload(downloadUrl, selectedAttempt.attempt_no);
      await finalizeChallengeShareInitiation(
        selectedChallenge,
        selectedAttempt,
        'download',
        selectedShareTarget
      );
      logWarn(
        'share.native_share.fallback_download',
        'Native share fell back to browser download',
        {
          session_id: sessionId,
          attempt_id: selectedAttempt.attempt_id,
          attempt_no: selectedAttempt.attempt_no,
          challenge_public_id: selectedChallenge.public_id,
          share_target: selectedShareTarget,
          native_share: nativeFileShareAvailable,
          status: error instanceof ApiError ? error.status : undefined,
        }
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="absolute inset-0 z-40 overflow-y-auto bg-[radial-gradient(circle_at_top,rgba(255,184,74,0.22),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(69,240,223,0.18),transparent_38%),linear-gradient(180deg,rgba(3,7,13,0.88),rgba(3,7,13,0.96))] px-4 pb-[calc(env(safe-area-inset-bottom,0px)+22px)] pt-[calc(env(safe-area-inset-top,0px)+16px)] backdrop-blur-2xl"
      role="dialog"
      aria-label="Share clips"
      aria-modal="true"
    >
      <div className="mx-auto flex min-h-full w-full max-w-[420px] flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">
              Session share
            </div>
            <h2 className="text-[28px] font-semibold leading-none text-white">
              Share clips
            </h2>
            <div className="text-[12px] text-white/65">
              {shareSession?.robot_name || 'PlayClaw'}
              {shareSession
                ? ` · ${shareSession.attempts.length} attempts`
                : ''}
            </div>
          </div>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 bg-white/10 text-white/80 shadow-[0_16px_32px_rgba(0,0,0,0.32)] backdrop-blur-xl hover:bg-white/15"
            onClick={onClose}
            aria-label="Close share clips"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="rounded-[30px] border border-white/12 bg-black/30 p-3 shadow-[0_28px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[26px] border border-white/10 bg-black/60">
            {isLoading && !shareSession ? (
              <div className="flex aspect-[9/16] items-center justify-center gap-3 bg-[radial-gradient(circle_at_top,rgba(255,184,74,0.18),transparent_35%),linear-gradient(180deg,rgba(8,10,16,0.95),rgba(8,10,16,1))] text-white/80">
                <LoaderCircle className="h-5 w-5 animate-spin" />
                <span className="text-[13px] font-medium">
                  Loading your clips...
                </span>
              </div>
            ) : loadError && !shareSession ? (
              <div className="flex aspect-[9/16] flex-col items-center justify-center gap-4 px-6 text-center text-white">
                <div className="space-y-2">
                  <div className="text-lg font-semibold">
                    Could not load your clips right now.
                  </div>
                  <div className="text-sm text-white/65">
                    Check back in a moment and try again.
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-10 rounded-full border border-white/15 bg-white/15 px-5 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-white/20"
                  onClick={() => {
                    void loadShareSession();
                  }}
                >
                  Retry
                </Button>
              </div>
            ) : selectedAttempt && isPreviewReadyComposite(selectedPreviewComposite) ? (
              <video
                key={selectedPreviewKey}
                className="aspect-[9/16] w-full bg-black object-cover"
                controls
                playsInline
                preload="metadata"
                poster={selectedPreviewComposite?.thumbnail_url ?? undefined}
                src={selectedPreviewComposite?.preview_url ?? undefined}
              />
            ) : (
              <div className="relative flex aspect-[9/16] flex-col items-center justify-center overflow-hidden px-6 text-center text-white">
                {selectedPreviewComposite?.thumbnail_url ? (
                  <img
                    src={selectedPreviewComposite.thumbnail_url}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover opacity-25"
                  />
                ) : null}
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,8,14,0.35),rgba(5,8,14,0.92))]" />
                <div className="relative z-10 flex max-w-[220px] flex-col items-center gap-3">
                  {selectedDisplayStatus === 'failed' ? (
                    <>
                      <div className="text-[20px] font-semibold">
                        {selectedChallenge ? 'Challenge unavailable' : 'Clip unavailable'}
                      </div>
                      <div className="text-[13px] leading-6 text-white/70">
                        {selectedChallenge
                          ? 'This challenge version could not be prepared.'
                          : 'This clip could not be prepared.'}
                      </div>
                    </>
                  ) : selectedDisplayStatus === 'ready' ? (
                    <>
                      <div className="text-[20px] font-semibold">Clip ready</div>
                      <div className="text-[13px] leading-6 text-white/70">
                        Preview is not available right now. Share unlocks as soon as a media URL is available.
                      </div>
                    </>
                  ) : (
                    <>
                      <LoaderCircle className="h-6 w-6 animate-spin text-amber-200" />
                      <div className="text-[20px] font-semibold">
                        {selectedChallenge
                          ? 'Preparing challenge version...'
                          : 'Rendering your clip...'}
                      </div>
                      <div className="text-[13px] leading-6 text-white/70">
                        {selectedChallenge
                          ? 'This version adds the live challenge URL and QR code.'
                          : 'This can take a short moment after the game ends.'}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">
                {selectedAttempt
                  ? `Attempt #${selectedAttempt.attempt_no + 1}`
                  : 'Clip status'}
              </div>
              <div className="text-[18px] font-semibold text-white">
                {selectedAttempt
                  ? formatResultLabel(selectedAttempt.result_kind)
                  : 'Your clips are still being prepared.'}
              </div>
              <div className="text-[12px] text-white/60">{statusDescription}</div>
            </div>
            <Badge
              className={cn(
                'border text-[10px] tracking-[0.2em]',
                selectedStatusToneClass
              )}
            >
              {selectedStatusLabel}
            </Badge>
          </div>

          {selectedChallenge ? (
            <div className="mt-4 rounded-[24px] border border-emerald-300/16 bg-emerald-400/10 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-100/80">
                Challenge link
              </div>
              <div className="mt-2 text-[17px] font-semibold text-white">
                {selectedChallenge.cta_headline}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {CHALLENGE_SHARE_LINK_OPTIONS.map((option) => {
                  const isSelected = option.target === selectedShareTarget;

                  return (
                    <button
                      key={option.target}
                      type="button"
                      aria-pressed={isSelected}
                      className={cn(
                        'rounded-full border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors',
                        isSelected
                          ? 'border-emerald-200/55 bg-emerald-200/20 text-emerald-50'
                          : 'border-white/12 bg-white/8 text-white/72 hover:bg-white/12'
                      )}
                      onClick={() => {
                        setSelectedShareTarget(option.target);
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-100/72">
                Tagged for {selectedShareTargetLabel}
              </div>
              {selectedShareLink ? (
                <a
                  href={selectedShareLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 block break-all text-[13px] leading-6 text-emerald-100/85 underline decoration-emerald-200/35 underline-offset-4"
                >
                  {selectedShareLink}
                </a>
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="rounded-full border border-emerald-200/35 bg-emerald-200/12 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-50 transition-colors hover:bg-emerald-200/18"
                  onClick={() => {
                    void handleCopyShareLink();
                  }}
                >
                  Copy tagged link
                </button>
                <div className="text-[12px] text-white/68">{challengeHint}</div>
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-[24px] border border-white/10 bg-white/[0.04] p-4 text-[12px] text-white/68">
              {challengeHint}
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-[28px] border border-white/10 bg-white/[0.06] p-3 shadow-[0_20px_44px_rgba(0,0,0,0.25)] backdrop-blur-xl">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
              Attempts
            </div>
            <div className="flex items-center gap-3">
              {hasPendingAttempts(
                shareSession,
                challengeByAttemptId,
                challengeStatusByAttemptId
              ) ? (
                <div className="flex items-center gap-2 text-[11px] text-white/60">
                  <span
                    className={cn(
                      'h-2.5 w-2.5 rounded-full bg-amber-300',
                      isRefreshing ? 'animate-pulse' : ''
                    )}
                  />
                  Checking for new clips
                </div>
              ) : null}
              <button
                type="button"
                className="rounded-full border border-white/12 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/72 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleRefresh}
                disabled={!canRefresh}
              >
                {isRefreshing ? 'Refreshing' : 'Refresh'}
              </button>
            </div>
          </div>
          {loadError && shareSession ? (
            <div className="rounded-2xl border border-amber-300/18 bg-amber-400/10 px-3 py-2 text-[12px] text-amber-50/88">
              Could not refresh your clips right now.
            </div>
          ) : null}
          <div className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none]">
            {(shareSession?.attempts || []).map((attempt) => {
              const isSelected = attempt.attempt_id === selectedAttemptId;
              const attemptChallenge = selectChallenge(attempt, challengeByAttemptId);
              const attemptPreviewComposite = selectPreviewComposite(attempt);
              const attemptShareComposite = selectShareComposite(attempt);
              const attemptChallengeStatus =
                challengeStatusByAttemptId[attempt.attempt_id] ?? 'idle';
              const attemptDisplayStatus = resolveDisplayStatus({
                previewComposite: attemptPreviewComposite,
                shareComposite: attemptShareComposite,
                challenge: attemptChallenge,
                challengeStatus: attemptChallengeStatus,
              });
              const thumbnailUrl =
                attemptPreviewComposite?.thumbnail_url ??
                attempt.composite?.thumbnail_url ??
                null;
              const durationMs =
                attemptShareComposite?.duration_ms ??
                attemptPreviewComposite?.duration_ms;
              const durationLabel = formatDurationLabel(durationMs);

              return (
                <button
                  key={attempt.attempt_id}
                  type="button"
                  className={cn(
                    'relative flex w-[148px] shrink-0 flex-col gap-3 overflow-hidden rounded-[24px] border px-3 pb-3 pt-2 text-left transition-all',
                    isSelected
                      ? 'border-white/30 bg-white/[0.16] shadow-[0_18px_36px_rgba(0,0,0,0.28)]'
                      : 'border-white/10 bg-white/[0.06] hover:border-white/20 hover:bg-white/10'
                  )}
                  onClick={() => handleAttemptSelect(attempt)}
                >
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-30"
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,8,14,0.18),rgba(5,8,14,0.92))]" />
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
                      #{attempt.attempt_no + 1}
                    </div>
                    <span
                      className={cn(
                        'rounded-full border px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.18em]',
                        displayStatusToneClass(attemptDisplayStatus)
                      )}
                    >
                      {formatDisplayStatusLabel(attemptDisplayStatus)}
                    </span>
                  </div>
                  <div className="relative z-10 mt-10 flex min-h-[72px] flex-col justify-end gap-1">
                    <div className="text-[16px] font-semibold text-white">
                      {formatResultLabel(attempt.result_kind)}
                    </div>
                    <div className="text-[11px] leading-5 text-white/65">
                      {durationLabel || 'Ready when the render finishes.'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-auto space-y-3 rounded-[28px] border border-white/10 bg-black/35 p-4 shadow-[0_20px_44px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          {showShareButton ? (
            <Button
              type="button"
              className="h-12 w-full rounded-full bg-white text-[12px] font-semibold uppercase tracking-[0.22em] text-black hover:bg-white/90"
              onClick={() => {
                void handleDownload();
              }}
              disabled={isPrimaryActionDisabled}
            >
              {isExporting || isPreparingNativeShare ? (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              ) : canUseNativeFileShare ? (
                <Share2 className="mr-2 h-4 w-4" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {isExporting ? exportInProgressLabel : primaryActionLabel}
            </Button>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-[12px] text-white/68">
              {challengeHint}
            </div>
          )}

          {shareFeedback ? (
            <div className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-white/65">
              {shareFeedback}
            </div>
          ) : null}

          <Button
            type="button"
            variant="ghost"
            className="h-11 w-full rounded-full border border-white/10 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/78 hover:bg-white/10"
            onClick={onClose}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareClipsSheet;
